import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import Chart from 'react-apexcharts';
import moment from 'moment';
import classes from './celmodReport.module.scss';
import Loader from '../../../_common/Loader/Loader';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Button from '../../../_common/Form/Buttons/Button';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import Utils from '../../../../utils/utils';
import Api from '../../../../api/api';

const tableHeader = [
  {
    name: 'Physician',
    path: 'physicianFullName',
  },
  {
    name: 'Active Revilimid Patient',
    path: 'active',
  },
  {
    name: '18 Months Therapy',
    path: 'therapy',
  },
  {
    name: 'Potential Relapse Risk',
    path: 'relapse',
  },
  {
    name: 'Expected Switch in 12 Months',
    path: 'therapySwitch',
  },
];

const chartTemplate = {
  series: [],

  options: {
    chart: {
      height: 350,
      type: 'bar',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      events: {},
    },

    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 2,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [],
      labels: {
        style: {
          fontSize: '12px',
        },
      },
    },
    legend: {
      position: 'bottom',
      markers: {
        width: 16,
        height: 16,
        radius: 50,
      },
    },

    grid: {
      show: true,
      strokeDashArray: 2,
    },
  },
};

const totalChartTemplate = {
  options: {
    chart: {
      type: 'donut',
      width: '100%',
    },

    labels: ['Active', '18M Therapy', 'Relapse', 'Switch'],

    colors: ['#4A3AFF', '#06b6d4', '#f59e0b', '#ef4444'],

    dataLabels: {
      enabled: false,
    },

    stroke: {
      width: 3,
    },

    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: '65%',
        },
      },
    },

    states: {
      active: { filter: { type: 'none' } },
    },

    legend: {
      show: true,
      position: 'right',
      formatter: (seriesName, opts) => {
        const value = opts.w.globals.series[opts.seriesIndex];

        return `
          <div style="display:flex;justify-content:space-between;font-size:14px;margin-left:5px;">
            <div style="display:flex;word-break:break-all;white-space:break-spaces;">${seriesName} </div>
            -
            <strong  style="margin-left:5px"> ${value}</strong>
          </div>
        `;
      },
      markers: {
        markers: {
          width: 16,
          height: 16,
          radius: 50,
        },
      },
      itemMargin: {
        vertical: 8,
      },
    },
  },

  series: [],
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

let timeout;

const CelmodReport = () => {
  const { query, setQuery } = useQuery();

  const firstLoad = useRef(true);

  const [filtersData, setFilterData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    physicianId: query.physicianId || '',
    page: query.page || 1,
    limit: query.limit || 15,
  });

  const [celmondData, setCelmondData] = useState({
    list: [],
    totalPage: 1,
  });

  const { list, totalPage } = celmondData;

  const [chart, setChart] = useState(chartTemplate);
  const [totalChart, setTotalChart] = useState(totalChartTemplate);
  const [chartLoading, chartLoadingToggle] = useState(false);

  const [loading, loadingToggle] = useState(false);

  const [physicians, setPhysicians] = useState([]);
  const [physiciansSearchValue, setPhysiciansSearchValue] = useState(query.physician || '');
  const [physiciansSearchLoading, physiciansSearchLoadingToggle] = useState(false);

  const topUpdate = async () => {
    chartLoadingToggle(true);
    const { data } = await Api.getCelmodTop({
      ...filtersData,
      start: moment(filtersData.start).toISOString(),
      end: filtersData.end
        ? moment(filtersData.end).toISOString()
        : moment(filtersData.start).add(1, 'day').toISOString(),
    });

    const newChart = _.cloneDeep(chartTemplate);

    newChart.series = [
      { name: 'Active', data: data.top.map((d) => d.active || 0), color: '#4A3AFF' },
      { name: '18M Therapy', data: data.top.map((d) => d.therapy || 0), color: '#06b6d4' },
      { name: 'Relapse', data: data.top.map((d) => d.relapse || 0), color: '#f59e0b' },
      { name: 'Switch', data: data.top.map((d) => d.therapySwitch || 0), color: '#ef4444' },
    ];

    newChart.options.xaxis.categories = data.top.map((d) => d.physicianFullName);

    const newTotalChart = _.cloneDeep(totalChartTemplate);

    newTotalChart.series = [
      _.sum(data.top.map((d) => +d.active || 0)),
      _.sum(data.top.map((d) => +d.therapy || 0)),
      _.sum(data.top.map((d) => +d.relapse || 0)),
      _.sum(data.top.map((d) => +d.therapySwitch || 0)),
    ];

    setChart(newChart);
    setTotalChart(newTotalChart);

    chartLoadingToggle(false);
  };

  const update = async (filters = filtersData) => {
    loadingToggle(true);
    const sendData = Utils.deleteEmptyKeys(filters);
    setQuery(sendData);

    const { data } = await Api.getCelmod({
      ...sendData,
      start: moment(filters.start).toISOString(),
      end: filters.end ? moment(filters.end).toISOString() : moment(filters.start).add(1, 'day').toISOString(),
    });

    setCelmondData({ list: data.data, totalPage: data._meta.pageCount });
    loadingToggle(false);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([update(), topUpdate()]);
      firstLoad.current = false;
    })();
  }, []);

  const searchPhysicians = (value) => {
    setPhysiciansSearchValue(value);

    clearTimeout(timeout);

    if (value.trim()) {
      physiciansSearchLoadingToggle(true);

      timeout = setTimeout(async () => {
        const { data: { data: { response } } } = await Api.getCelmodPhysicians({ limit: 100, name: value });

        setPhysicians(response.map((r) => ({ ...r, name: `${r.firstName} ${r.lastName}` })));

        physiciansSearchLoadingToggle(false);
      }, 400);
    } else {
      setPhysicians([]);
      physiciansSearchLoadingToggle(false);
    }
  };

  const changeRequestData = (path, value) => {
    setFilterData((prev) => {
      const newFilters = { ...prev };

      if (path === 'date') {
        newFilters.start = value[0];
        newFilters.end = value[1];
      } else if (path === 'physicianId') {
        newFilters.physicianId = value.id;
        newFilters.physician = value.name;

        setPhysiciansSearchValue(value.name);
      } else {
        newFilters[path] = value;
      }

      return newFilters;
    });
  };

  const onChangePageData = async (path, count) => {
    const sendingData = { ...filtersData, [path]: count };

    if (path === 'limit') {
      sendingData.page = 1;
    }

    setFilterData(sendingData);

    update(sendingData);
  };

  const clearFilters = () => {
    const sendingData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      physicianId: '',
      page: 1,
      limit: filtersData.limit,
    };

    setFilterData(sendingData);
    setPhysiciansSearchValue('');
    update(sendingData);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.filters_wrapper}>
        <RangeDatePicker
          currentDate={filtersData}
          onChangeDate={(date) => changeRequestData('date', date)}
          showLastYear
        />

        <div className={classes.select_wrapper}>
          <SelectWithSearch
            label="All physician"
            searchValue={physiciansSearchValue}
            options={physicians}
            optionKey=""
            loading={physiciansSearchLoading}
            onChange={(value) => searchPhysicians(value)}
            onSelectOption={(option, searchValue) => changeRequestData('physicianId', option || {}, searchValue)}
            zIndex={30}
          />
        </div>

        <div className={classes.btns_wrapper}>
          <Button
            title="Update"
            onClick={() => {
              update();
              topUpdate();
            }}
          />

          <Button
            title="Clear"
            clearButton
            onClick={clearFilters}
          />
        </div>
      </div>

      <div className={classes.dashboard_content_wrapper}>
        {firstLoad.current
          ? <Loader />

          : (
            <>
              <div className={classes.dashboard_charts_wrapper}>
                <div className={classes.dashboard_left_chart}>
                  {chartLoading
                    ? <Loader />

                    : (
                      (chart.series[0].data.length
                        ? (
                          <>
                            <h4>Top CELMoD</h4>

                            <Chart
                              options={chart.options}
                              series={chart.series}
                              type="bar"
                              height={240}
                            />
                          </>
                        )

                        : <div className={classes.chart_is_empty}>No Data Found</div>)
                    )}
                </div>

                <div className={`${classes.dashboard_right_chart} donat_chart`}>
                  {chartLoading
                    ? <Loader />

                    : (
                      (chart.series[0].data.length
                        ? (
                          <>
                            <h4>Total CELMoD</h4>

                            <Chart
                              options={totalChart.options}
                              series={totalChart.series}
                              type="donut"
                              height={220}
                            />
                          </>
                        )

                        : <div className={classes.chart_is_empty}>No Data Found</div>)
                    )}
                </div>
              </div>

              <div className={classes.table_wrapper}>
                {loading && <Loader className={classes.loader} /> }

                <TableAndPagination
                  header={tableHeader}
                  data={list}
                  onChange={onChangePageData}
                  totalPage={totalPage}
                  page={filtersData.page}
                  limit={filtersData.limit}
                />
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default CelmodReport;
